import supabase, { isSupabaseConfigured } from './supabaseClient';

function unavailable() {
  return { data: null, error: new Error('Supabase is not configured. Add the VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY values.') };
}

export async function signInCustomer(email, password) {
  const cleanEmail = String(email || '').trim().toLowerCase();

  // 1. Check Supabase `customer_accounts` table
  if (isSupabaseConfigured) {
    const { data: customer } = await supabase
      .from('customer_accounts')
      .select('*')
      .eq('email', cleanEmail)
      .eq('password', password)
      .maybeSingle();

    if (customer) {
      const sessionUser = {
        id: customer.id,
        email: customer.email,
        full_name: customer.full_name,
        user_metadata: { full_name: customer.full_name, mobile: customer.mobile },
        role: customer.role || 'customer',
      };
      localStorage.setItem('trishul_customer_session', JSON.stringify(sessionUser));
      return { data: { user: sessionUser, session: { user: sessionUser } }, error: null };
    }

    // Fallback: check standard Supabase Auth
    const authResult = await supabase.auth.signInWithPassword({ email: cleanEmail, password });
    if (!authResult.error && authResult.data?.user) {
      localStorage.removeItem('trishul_customer_session');
      return authResult;
    }
  }

  // 2. Check local storage fallback
  const localCustomers = JSON.parse(localStorage.getItem('trishul_local_customers') || '[]');
  const localMatch = localCustomers.find((c) => c.email === cleanEmail && c.password === password);
  if (localMatch) {
    localStorage.setItem('trishul_customer_session', JSON.stringify(localMatch));
    return { data: { user: localMatch, session: { user: localMatch } }, error: null };
  }

  return { data: null, error: new Error('Invalid email or password.') };
}

export async function signInAdmin(username, password) {
  if (!isSupabaseConfigured) return unavailable();

  const cleanInput = String(username || '').trim();
  const cleanPassword = String(password || '');

  if (!cleanInput || !cleanPassword) {
    return { data: null, error: new Error('Please enter both username/email and password.') };
  }

  let loginEmail = null;

  // 1. If user typed an email directly
  if (cleanInput.includes('@')) {
    loginEmail = cleanInput.toLowerCase();
  } else {
    // 2. Resolve username to email via Supabase RPC
    try {
      const { data, error } = await supabase.rpc('get_admin_login_email', { login_username: cleanInput });
      if (!error && data) {
        loginEmail = data;
      }
    } catch (e) {
      // ignore RPC error
    }

    // 3. Fallback for known admin usernames
    if (!loginEmail) {
      const lower = cleanInput.toLowerCase();
      if (lower === 'trishuladmin' || lower === 'trishul' || lower === 'admin') {
        loginEmail = 'trishultoursandtravels2315@gmail.com';
      } else if (lower === 'inspiration' || lower === 'inspirationsoft' || lower === 'superadmin') {
        loginEmail = 'inspirationsoftsolutions@gmail.com';
      }
    }
  }

  if (!loginEmail) {
    return { data: null, error: new Error('Invalid admin username or password.') };
  }

  // Attempt authentication with Supabase
  const result = await supabase.auth.signInWithPassword({ email: loginEmail, password: cleanPassword });
  if (result.error || !result.data?.user) {
    return result;
  }

  const user = result.data.user;

  // Check admin role
  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  const role = String(profile?.role || user.user_metadata?.role || user.app_metadata?.role || '').toLowerCase();
  const userEmail = (user.email || '').toLowerCase();
  const profileUsername = String(profile?.username || '').toLowerCase();

  const isDesignatedAdmin =
    ['inspirationsoftsolutions@gmail.com', 'trishultoursandtravels2315@gmail.com'].includes(userEmail) ||
    ['admin', 'superadmin'].includes(role) ||
    ['trishuladmin', 'inspiration'].includes(profileUsername);

  if (!isDesignatedAdmin) {
    await supabase.auth.signOut();
    return { data: null, error: new Error('This account does not have admin access.') };
  }

  // Auto-sync profile if not present or role missing
  if (!profile || !['admin', 'superadmin'].includes(role)) {
    try {
      await supabase.from('profiles').upsert({
        id: user.id,
        full_name: profile?.full_name || user.user_metadata?.full_name || (userEmail.includes('inspiration') ? 'Inspiration Admin' : 'Trishul Admin'),
        username: profile?.username || (userEmail.includes('inspiration') ? 'inspiration' : 'trishuladmin'),
        role: userEmail.includes('inspiration') ? 'superadmin' : 'admin',
      });
    } catch (e) {
      // silent catch
    }
  }

  return result;
}

export async function registerCustomer({ fullName, email, mobile, password }) {
  const cleanEmail = String(email || '').trim().toLowerCase();
  const cleanPhone = String(mobile || '').trim();

  // Try saving to Supabase `customer_accounts` table
  if (isSupabaseConfigured) {
    const { data: existing } = await supabase
      .from('customer_accounts')
      .select('*')
      .eq('email', cleanEmail)
      .maybeSingle();

    if (existing) {
      return { data: null, error: new Error('An account with this email already exists.') };
    }

    const { data: newCustomer, error: insertError } = await supabase
      .from('customer_accounts')
      .insert({
        full_name: fullName,
        email: cleanEmail,
        mobile: cleanPhone,
        password: password,
        role: 'customer',
      })
      .select()
      .maybeSingle();

    if (!insertError && newCustomer) {
      const sessionUser = {
        id: newCustomer.id,
        email: newCustomer.email,
        full_name: newCustomer.full_name,
        user_metadata: { full_name: newCustomer.full_name, mobile: newCustomer.mobile },
        role: 'customer',
      };
      localStorage.setItem('trishul_customer_session', JSON.stringify(sessionUser));
      return { data: { user: sessionUser, session: { user: sessionUser } }, error: null };
    }
  }

  // Fallback to local customer storage
  const localCustomers = JSON.parse(localStorage.getItem('trishul_local_customers') || '[]');
  const existingLocal = localCustomers.find((c) => c.email === cleanEmail);
  if (existingLocal) {
    return { data: null, error: new Error('An account with this email already exists.') };
  }

  const newLocal = {
    id: `cust_${Date.now()}`,
    full_name: fullName,
    email: cleanEmail,
    mobile: cleanPhone,
    password: password,
    role: 'customer',
  };
  localCustomers.push(newLocal);
  localStorage.setItem('trishul_local_customers', JSON.stringify(localCustomers));
  localStorage.setItem('trishul_customer_session', JSON.stringify(newLocal));

  return { data: { user: newLocal, session: { user: newLocal } }, error: null };
}

export async function signInWithGoogle() {
  if (!isSupabaseConfigured) return unavailable();
  return supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${window.location.origin}/customer/dashboard`,
    },
  });
}

export async function signOut() {
  localStorage.removeItem('trishul_customer_session');
  if (isSupabaseConfigured) {
    await supabase.auth.signOut();
  }
  return { error: null };
}

export async function getCurrentUser() {
  const localSession = localStorage.getItem('trishul_customer_session');
  if (localSession) {
    try {
      const user = JSON.parse(localSession);
      return { user, error: null };
    } catch {
      localStorage.removeItem('trishul_customer_session');
    }
  }
  if (!isSupabaseConfigured) return { user: null, error: unavailable().error };
  const { data, error } = await supabase.auth.getUser();
  return { user: data?.user || null, error };
}

export async function getCurrentProfile(userId) {
  const localSession = localStorage.getItem('trishul_customer_session');
  if (localSession) {
    try {
      const user = JSON.parse(localSession);
      if (user.id === userId) {
        return { data: { id: user.id, full_name: user.full_name, mobile: user.mobile, role: user.role || 'customer' }, error: null };
      }
    } catch {
      // ignore
    }
  }
  if (!isSupabaseConfigured) return { data: null, error: unavailable().error };
  const { data: custProfile } = await supabase.from('customer_accounts').select('*').eq('id', userId).maybeSingle();
  if (custProfile) return { data: custProfile, error: null };

  const { data: profile } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (profile) return { data: profile, error: null };

  // Fallback: Check active auth user if profile doesn't exist in DB yet
  const { data: authData } = await supabase.auth.getUser();
  const authUser = authData?.user;
  if (authUser && authUser.id === userId) {
    const email = (authUser.email || '').toLowerCase();
    if (email === 'inspirationsoftsolutions@gmail.com') {
      return { data: { id: userId, full_name: 'Inspiration Admin', username: 'inspiration', role: 'superadmin', email }, error: null };
    }
    if (email === 'trishultoursandtravels2315@gmail.com' || email.includes('trishul')) {
      return { data: { id: userId, full_name: 'Trishul Admin', username: 'trishuladmin', role: 'admin', email }, error: null };
    }
  }

  return { data: null, error: null };
}
